import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

interface ChatMessage {
  sender: 'user' | 'bot';
  text: string;
  files?: { name: string }[]; // Save only file names
  imageUrl?: string;          // Data-URI of a pasted/attached image, shown inline
  fileAttachment?: { name: string; dataUri: string }; // Uploaded file, re-downloadable
}

/** View mode a conversation was started in — drives the sidebar icon. */
export type ConversationMode = 'standard' | 'code' | 'writing' | 'image';

interface ChatHistory {
  id: string;
  title: string;
  mode?: ConversationMode;
  messages: ChatMessage[];
}

@Injectable({
  providedIn: 'root'
})
export class HistoryService {
  private readonly STORAGE_KEY = 'chat_history';
  historyUpdated = new Subject<void>();

  constructor() {}

  getConversations(): ChatHistory[] {
    try {
      const history = localStorage.getItem(this.STORAGE_KEY);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Error parsing history:', error);
      return [];
    }
  }

  saveConversation(id: string, title: string, mode: ConversationMode = 'standard'): void {
    const conversations = this.getConversations();
  
    if (!conversations.some(conv => conv.id === id)) {
      // Add new conversation to the top (most recent)
      conversations.unshift({
        id,
        title,
        mode,
        messages: []
      });
      this.updateLocalStorage(conversations);
    }
  }
  

  deleteConversation(id: string): void {
    const conversations = this.getConversations().filter(conv => conv.id !== id);
    this.updateLocalStorage(conversations);
  }

  /** Returns the stored mode for a conversation, defaulting to 'standard'. */
  getConversationMode(id: string): ConversationMode {
    const conv = this.getConversations().find(c => c.id === id);
    return conv?.mode ?? 'standard';
  }

  /** Updates the mode of an existing conversation in place. */
  setConversationMode(id: string, mode: ConversationMode): void {
    const conversations = this.getConversations();
    const conv = conversations.find(c => c.id === id);
    if (conv && conv.mode !== mode) {
      conv.mode = mode;
      this.updateLocalStorage(conversations);
    }
  }

  // Updated to optionally store files and move conversation to top
  appendMessage(id: string, message: string, sender: 'user' | 'bot', files?: { name: string }[], imageUrl?: string, fileAttachment?: { name: string; dataUri: string }): void {
    const conversations = this.getConversations();
    const conversationIndex = conversations.findIndex(conv => conv.id === id);

    if (conversationIndex !== -1) {
      // Add the message to the conversation
      conversations[conversationIndex].messages.push({ sender, text: message, files, imageUrl, fileAttachment });
      
      // Move the conversation to the top (most recent)
      const conversation = conversations.splice(conversationIndex, 1)[0];
      conversations.unshift(conversation);
      
      this.updateLocalStorage(conversations);
    }
  }

  private updateLocalStorage(conversations: ChatHistory[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(conversations));
      this.historyUpdated.next();
    } catch (error) {
      console.error('Error updating local storage:', error);
    }
  }
}
